import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { fetchGetCompanyInfo } from "reducers/informationSlice";
import { INDUSTRY_OPTIONS } from "common/enums/Industry.enum";
import { UserTypeEnum } from "common/enums/UserType.enum";
import { Button, message, Modal } from "antd";
import { UTCToJapanTime } from "common/helper/time.helper";
import { companyInfoStatusMap } from "common/helper/text.helper";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { API_ENDPOINTS, FILE_BASE_URL } from "config/apiConfig";
import { IconKeyboardArrowRightBlue, IconSmsBlue } from "assets/svg";
import { axiosInstance } from "config/axiosConfig";
import { NoteTypeEnum } from "common/enums/NoteType.enum";
import { CompanyInfoStatusEnum } from "common/enums/CompanyInfoStatus.enum";
import { useUpdateCompanyMutation } from "reducers/companyApi";
import CustomConfirmModal from "components/CustomConfirmModal";
import { useCommonContext } from "contexts/CommonContext";

type AdminNote = {
  id: string | number;
  created_at: string;
  content: string;
};

const CompanyManagementViewPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { companyReviewData } = useSelector(
    (state: RootState) => state.information,
  );
  const { companyCode } = useParams();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState<"image" | "pdf">("image");
  const [modalTitle, setModalTitle] = useState("");
  const [totalRequest, setTotalRequest] = useState(0);
  const [noteList, setNoteList] = useState<AdminNote[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openApprovalModal, setOpenApprovalModal] = useState(false);

  if (!companyCode) {
    return <></>;
  }

  const [updateCompany] = useUpdateCompanyMutation();
  const onUpdateCompany = async () => {
    await updateCompany({ companyCode, status: CompanyInfoStatusEnum.FROZEN });
    setOpenConfirmModal(false);
    dispatch(
      fetchGetCompanyInfo(companyCode, {
        product_overview: 1,
        transaction_overview: 1,
        admin_review: 1,
      }),
    );
  };

  const onApprovedCompany = async () => {
    await updateCompany({
      companyCode,
      status: CompanyInfoStatusEnum.APPROVED,
    });
    setOpenApprovalModal(false);
    dispatch(
      fetchGetCompanyInfo(companyCode, {
        product_overview: 1,
        transaction_overview: 1,
        admin_review: 1,
      }),
    );
  };

  useEffect(() => {
    dispatch(
      fetchGetCompanyInfo(companyCode, {
        product_overview: 1,
        transaction_overview: 1,
        admin_review: 1,
      }),
    );
  }, [dispatch, companyCode]);

  useEffect(() => {
    if (companyReviewData) {
      axiosInstance
        .get(API_ENDPOINTS.CONVERSATION_INQUIRY, {
          params: {
            seller_id: companyReviewData?.data.id,
            page: 1,
          },
        })
        .then((response) => {
          setTotalRequest(response.data.data.meta.total_items);
        });
    }
  });

  useEffect(() => {
    if (!companyReviewData?.data?.id) return;
    axiosInstance
      .get(API_ENDPOINTS.NOTE_LIST, {
        params: {
          object_id: companyReviewData.data.id,
          object_type: NoteTypeEnum.COMPANY,
        },
      })
      .then((res) => {
        setNoteList(res.data.data || []);
      });
    setHeaderTitle("ユーザー管理");
  }, []);

  const createdAt = () => {
    if (!companyReviewData?.data.created_at) return "___";
    const { year, month, day, hour, minute } = UTCToJapanTime(
      companyReviewData?.data.created_at,
    );
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const lastLogin = () => {
    if (!companyReviewData?.data.last_login) return "___";
    const { year, month, day, hour, minute } = UTCToJapanTime(
      companyReviewData?.data.last_login,
    );
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const cancelRate = () => {
    if (companyReviewData?.data.transaction_overview?.total === 0)
      return "まだ取引がありません";
    const cancelRate =
      companyReviewData?.data.transaction_overview?.cancel_number /
      companyReviewData?.data.transaction_overview?.total;
    return `${cancelRate * 100}%`;
  };

  const getFileType = (url: string): "image" | "pdf" => {
    if (!url) return "image";
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes(".pdf") || lowerUrl.includes("pdf")) {
      return "pdf";
    }
    return "image";
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setPreviewUrl("");
    setModalTitle("");
  };

  const FileActionButtons = ({
    url,
    filename,
  }: {
    url: string;
    filename: string;
  }) => {
    if (!url) {
      return <span className="text-gray-400">ファイルなし</span>;
    }

    return (
      <div className="flex flex-col md:flex-row items-center gap-2">
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handlePreview(url, filename)}
          className="p-0 h-auto"
        >
          プレビュー表示
        </Button>
        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(url, filename)}
          className="p-0 h-auto"
        >
          ダウンロード
        </Button>
      </div>
    );
  };

  const handlePreview = (url: string, title: string) => {
    if (!url) {
      message.warning("ファイルのURLが見つかりません");
      return;
    }
    const fullUrl = `${FILE_BASE_URL}/${url}`;
    setPreviewUrl(fullUrl);
    setPreviewType(getFileType(fullUrl));
    setModalTitle(title);
    setIsModalVisible(true);
  };

  // Download file
  const handleDownload = async (url: string, filename: string) => {
    try {
      if (!url) {
        message.warning("ファイルのURLが見つかりません");
        return;
      }

      if (!url.includes("http")) {
        url = `${FILE_BASE_URL}/${url}`;
      }

      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      message.success("ダウンロードを開始しました");
    } catch (error) {
      message.error("ダウンロードに失敗しました");
    }
  };
  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "ユーザー管理",
            link: "/admin/company-management",
          },
          {
            label: `ユーザー詳細（ID: #${companyCode}）`,
            isCurrent: true,
          },
        ]}
      />

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                基本情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ユーザーID
            </div>
            <div className="flex-1 p-4 break-all">#{companyCode}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人名
            </div>
            <div className="flex-1 p-4 break-all">
              {companyReviewData?.data.company_name}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              所在地
            </div>
            <div className="flex-1 p-4 break-all">
              {companyReviewData?.data.address}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              業種
            </div>
            <div className="flex-1 p-4 break-all">
              {" "}
              {INDUSTRY_OPTIONS[companyReviewData?.data.industry]?.label}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              担当者名
            </div>
            <div className="flex-1 p-4 break-all">
              {" "}
              {companyReviewData?.data.contact_first_name +
                " " +
                companyReviewData?.data.contact_last_name}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              電話番号
            </div>
            <div className="flex-1 p-4 break-all">
              {" "}
              {companyReviewData?.data.phone_number}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ユーザー種別
            </div>
            {companyReviewData?.data.type === UserTypeEnum.COMPANY_BUYER ? (
              <div className="flex-1 p-4 break-all">買い手</div>
            ) : (
              ""
            )}
            {companyReviewData?.data.type === UserTypeEnum.COMPANY_SELLER ? (
              <div className="flex-1 p-4 break-all">売り手</div>
            ) : (
              ""
            )}
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              登録日
            </div>
            <div className="flex-1 p-4 break-all"> {createdAt()} </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              最終ログイン
            </div>
            <div className="flex-1 p-4 break-all"> {lastLogin()} </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ステータス
            </div>
            <div className="flex-1 p-4 break-all">
              {companyInfoStatusMap[companyReviewData?.data.status] || ""}{" "}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              メールアドレス
            </div>
            <div className="flex-1 p-4 break-all">
              {companyReviewData?.data.email}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] min-w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              パスワード
            </div>
            <div className="flex-1 p-4 break-all">●●●●●●●●●●●●●●●●</div>
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-2.5">
        <Link
          to={`/admin/company-management/${companyCode}/edit`}
          type="primary"
          className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold rounded-[6px] flex justify-center items-center text-white"
        >
          検索する
        </Link>
        {companyReviewData?.data.status !== CompanyInfoStatusEnum.FROZEN ? (
          <div
            className="cursor-pointer flex items-center justify-center min-w-full md:min-w-[340px] rounded-[6px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-red-600 text-red-600 text-lg font-bold hover:bg-red-600 hover:text-white"
            onClick={() => setOpenConfirmModal(true)}
          >
            凍結する
          </div>
        ) : (
          <div
            className="cursor-pointer flex items-center justify-center min-w-full md:min-w-[340px] rounded-[6px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
            onClick={() => setOpenApprovalModal(true)}
          >
            凍結を解除する
          </div>
        )}
      </div>

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                本人確認・法人確認
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          {companyReviewData?.data.type === UserTypeEnum.COMPANY_SELLER && (
            <div className="flex border-b border-b-neutral-border">
              <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
                マイナンバーカード
              </div>
              <div className="flex-1 p-4">
                <FileActionButtons
                  url={companyReviewData?.data.number_card_url}
                  filename="マイナンバーカード"
                />
              </div>
            </div>
          )}
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人登記簿謄本
            </div>
            <div className="flex-1 p-4">
              <FileActionButtons
                url={companyReviewData?.data.corp_regist_cert_url}
                filename="法人登記簿謄本"
              />
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              審査担当者
            </div>
            <div className="flex-1 p-4">
              {companyReviewData?.data?.admin_review?.name} (
              {companyReviewData?.data?.admin_review?.username})
            </div>
          </div>
        </div>
      </div>

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                過去の出品・取引情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              出品件数
            </div>
            <div className="flex p-4 justify-between w-[79%]">
              <span>{companyReviewData?.data.product_overview?.total}件</span>
              <Link
                to={`/admin/company-management/${companyReviewData?.data.id}/products`}
                state={{ companyCode: companyReviewData?.data.code }}
                className="text-primary"
              >
                出品一覧を見る <IconKeyboardArrowRightBlue className="inline" />
              </Link>
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              取引成立数
            </div>
            <div className="flex p-4 justify-between w-[79%]">
              <span>
                {companyReviewData?.data.transaction_overview?.completed_number}
                件
              </span>
              <Link
                to={`/admin/company-management/${companyReviewData?.data.id}/transactions?type=${companyReviewData?.data.type}`}
                state={{ companyCode: companyReviewData?.data.code }}
                className="text-primary"
              >
                取引履歴を見る <IconKeyboardArrowRightBlue className="inline" />
              </Link>
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              キャンセル率
            </div>
            <div className="flex-1 p-4">{cancelRate()}</div>
          </div>
        </div>
      </div>

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                お問い合わせ情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              お問い合わせ件数
            </div>
            <div className="flex p-4 justify-between w-[79%]">
              <span>{totalRequest}件</span>
              <Link
                to={`/admin/company-management/${companyReviewData?.data.id}/requests`}
                state={{ companyCode: companyReviewData?.data.code }}
                className="text-primary"
              >
                お問い合わせ一覧を見る{" "}
                <IconKeyboardArrowRightBlue className="inline" />
              </Link>
            </div>
          </div>
        </div>
        <Link
          to={`/admin/company-management/${companyCode}/add-request`}
          className="text-primary"
        >
          <IconSmsBlue className="inline" /> 新規でお問い合わせする
        </Link>
      </div>

      {/* table 操作ログ */}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                操作ログ
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          {noteList.length === 0 ? (
            <div className="flex justify-center items-center p-8 text-neutral-500">
              操作ログがありません
            </div>
          ) : (
            noteList.map((note: AdminNote, idx: number) => {
              const { year, month, day, hour, minute } = UTCToJapanTime(
                note.created_at,
              );
              return (
                <div
                  className={`flex ${idx !== noteList.length - 1 ? "border-b border-b-neutral-border" : ""}`}
                  key={note.id}
                >
                  <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
                    {`${year}/${month}/${day} ${hour}:${minute}`}
                  </div>
                  <div className="flex-1 p-4">{note.content}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <Link
        to={"/admin/company-management"}
        className="flex text-primary font-bold justify-center items-center w-[340px] min-h-[60px] px-5 py-2.5 gap-[10px] rounded-md bg-primary-light"
      >
        一覧へ戻る
      </Link>

      {/* File Preview Modal */}
      <Modal
        className="file-preview-modal"
        title={modalTitle}
        open={isModalVisible}
        onCancel={handleModalClose}
        closeIcon={null}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <div
            key={1}
            className="flex justify-center flex-col items-center gap-[10px]"
          >
            <button
              key="download"
              onClick={() => handleDownload(previewUrl, "test")}
              className="w-full md:w-80 min-h-14 px-5 py-2.5 rounded-md flex justify-center items-center text-white text-lg font-bold font-['Noto_Sans_JP'] leading-7 bg-primary"
            >
              ダウンロードする
            </button>
            <button
              key="close"
              onClick={handleModalClose}
              className="text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug"
            >
              戻る
            </button>
          </div>,
        ]}
      >
        {previewUrl && (
          <div className="w-full md:h-[70vh]">
            {previewType === "pdf" ? (
              // PDF Viewer
              <iframe
                src={`${previewUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title="PDF Viewer"
              />
            ) : (
              // Image Viewer
              <div className="flex justify-center items-center h-full bg-gray-50">
                <img
                  src={previewUrl}
                  alt={modalTitle}
                  style={{
                    maxWidth: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    message.error("画像の読み込みに失敗しました");
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
      <CustomConfirmModal
        open={openConfirmModal}
        title="このユーザーを
          凍結してもよろしいですか？"
        description={`このユーザーのアカウントを凍結すると、
          ログイン・出品・購入・メッセージなど、
          全ての機能が停止します。\n
          ※凍結状態は解除されるまで自動で復帰しません。
          ※現在の取引には影響する可能性があります。`}
        onOk={onUpdateCompany}
        onCancel={() => setOpenConfirmModal(false)}
        okText="ユーザーを凍結する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
      <CustomConfirmModal
        open={openApprovalModal}
        title="このユーザーの凍結を
          解除してもよろしいですか？"
        description={`凍結状態を解除すると、このユーザーは
          通常通りログインできるようになり、
          取引やチャットなど、すべての機能が再び利用可能になります。\n`}
        note={`
          ※現在の凍結理由や状況をご確認のうえ、慎重に対応してください。
          ※凍結解除は即時反映されます。`}
        onOk={onApprovedCompany}
        onCancel={() => setOpenApprovalModal(false)}
        okText="凍結を解除する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
    </div>
  );
};

export default CompanyManagementViewPage;
